import Router, { NextRouter } from "next/router";
import { ConditionalNextSlug } from "@dreambigger/shared/src/types/acquire"
import { message } from "antd";
import { Conditional } from "@dreambigger/conditional";

/**
 * Authorized-advance tracking for the forward-jump guard (see step-router.tsx).
 *
 * A routing-driven advance (the user submitted a step and `changeStepForward`
 * chose the destination) is legitimate even when it skips over steps already
 * in `visitedPath` — that's exactly a conditional-branch change, which the
 * deferred-branching algorithm is meant to prune. A browser back/forward jump
 * to the same destination is NOT legitimate and corrupts the path. The two are
 * identical at the array-index level, so the guard relies on this flag to tell
 * them apart.
 *
 * Slug-scoped (not consume-once) on purpose: the application cache refreshes
 * after a submit and re-runs the guard effect, so a one-shot boolean would be
 * consumed on the first run and wrongly block the second. Matching on the
 * destination slug survives those re-renders and only authorizes the one step
 * routing actually advanced to.
 */
let authorizedAdvanceSlug: string | null = null;

export const markAuthorizedAdvance = (slug?: string) => {
  authorizedAdvanceSlug = slug ?? null;
};

export const isAuthorizedAdvance = (slug: string): boolean =>
  authorizedAdvanceSlug !== null && authorizedAdvanceSlug === slug;

export const changeStepForward = (nextSlug?: string | ConditionalNextSlug, inputFields?: any) => {
  const router: NextRouter = Router;

  // Mark the destination as a routing-approved advance, then navigate. The
  // forward-jump guard consumes the mark on the destination's mount.
  const navigateTo = (slug?: string) => {
    markAuthorizedAdvance(slug);
    router.push({
      query: {
        ...router.query,
        stepSlug: slug,
      },
    });
  };

  if (!nextSlug) {
    message.error("There was an error moving to the next step. Please contact support.");
    return;
  }

  //CASE 1 -- NEXTSLUG HAS BEEN CONFIGURED AS A STRING
  if (typeof nextSlug === 'string') {
    navigateTo(nextSlug);
    return;
  }

  //CASE 2 -- NEXTSLUG HAS BEEN CONFIGURED AS A CONDITIONAL SLUG

  //Check for immediate configuration errors
  if (!nextSlug.matchFieldKey || (!inputFields && !nextSlug.defaultSlug)) {
    message.error("There was an error moving to the next step. Please contact support.");
    return;
  }

  const { matchFieldKey, matchFieldList, defaultSlug }  = nextSlug;

  //If no inputFields have been provided, route the user to a defaultSlug.
  if (!inputFields) {
    navigateTo(defaultSlug);
    return;
  }

  //Search JSON-configured list (matchFieldList) for a fieldValue that matches the value
  //passed in from the inputField specifically tagged for Step by matchFieldKey.
  //IE: inputValue[matchFieldKey] "state" might match up with fieldValue "CA" and lead to a slug.
  const matchItem = matchFieldList.find((listItem) =>
  Conditional.evaluate({
    conditions: {
      [listItem.matchFieldKey || matchFieldKey]: listItem.fieldValue,
    },
    data: inputFields,
  })
);

  //If there is no match but there is a defaultSlug, route the user there.
  if(!matchItem && defaultSlug) {
    navigateTo(defaultSlug);
    return;
  }

  //If there is no match and no configured defaultSlug, alert the user.
  if(!matchItem) {
    message.error("There was an error moving to the next step. Please contact support.")
    return;
  }

  //Otherwise, there is a match! Route the user to the corresponding slug.
  navigateTo(matchItem.slug);
  return;
}
